/**
 * Email Groups — admin manages audience segments (Email v2 Phase 2).
 *
 * Pro-only. Inline create + rename + delete. Auto "All members" group
 * is always at the top; never deletable / renameable. Server seeds
 * Parents + Coaches + Sponsors on first access for new Pro clubs.
 *
 * Polished UX/UI 2026-05-29 — replaces the earlier loud
 * "5-buttons-in-the-header" pattern with the shared EmailNav + the
 * .email-page-shell scoped button/header styles.
 */
import { useState, useEffect, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  listGroups,
  createGroup,
  renameGroup,
  deleteGroup,
  EmailGroup,
} from '../api/client';
import EmailNav from '../components/EmailNav';
import './EmailGroups.css';

export default function EmailGroups() {
  const navigate = useNavigate();
  const { plan } = useAuth();

  const [groups, setGroups] = useState<EmailGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [creating, setCreating] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [createBusy, setCreateBusy] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  useEffect(() => {
    if (plan !== 'pro') return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan]);

  const load = async () => {
    setError('');
    setLoading(true);
    try {
      const res = await listGroups();
      setGroups(res.groups);
    } catch (err: any) {
      setError(err?.message || 'Failed to load groups');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    const name = newGroupName.trim();
    if (!name) return;
    setCreateBusy(true);
    try {
      await createGroup(name);
      setNewGroupName('');
      setCreating(false);
      await load();
    } catch (err: any) {
      setError(err?.message || 'Failed to create group');
    } finally {
      setCreateBusy(false);
    }
  };

  const handleSaveRename = async (groupId: string) => {
    const name = editName.trim();
    if (!name) {
      setEditingId(null);
      setEditName('');
      return;
    }
    try {
      await renameGroup(groupId, name);
      setGroups(prev => prev.map(g => g.groupId === groupId ? { ...g, name } : g));
      setEditingId(null);
      setEditName('');
    } catch (err: any) {
      setError(err?.message || 'Failed to rename group');
    }
  };

  const handleDelete = async (group: EmailGroup) => {
    if (!confirm(
      `Delete "${group.name}"? Subscribers in this group will keep their other group memberships ` +
      `but won't receive sends targeted at this group anymore.`
    )) return;
    try {
      await deleteGroup(group.groupId);
      await load();
    } catch (err: any) {
      setError(err?.message || 'Failed to delete group');
    }
  };

  if (plan !== 'pro') {
    return (
      <div className="email-page-shell">
        <EmailNav />
        <div className="page-head">
          <div>
            <h1>Groups</h1>
            <p className="subtitle">Pro tier feature.</p>
          </div>
        </div>
        <div className="card-surface empty-state">
          <h3>Groups are a Pro feature</h3>
          <p>
            Groups let you segment your audience — send the U12 parents about
            training, the coaches about the meeting, the sponsors about the
            dinner. All separate, all properly targeted.
          </p>
          <button className="ebtn ebtn-primary" onClick={() => navigate('/settings')}>
            Upgrade to Pro
          </button>
        </div>
      </div>
    );
  }

  const explicitGroupCount = groups.filter(g => !g.isAuto).length;

  return (
    <div className="email-page-shell">
      <EmailNav />

      <div className="page-head">
        <div>
          <h1>Groups</h1>
          <p className="subtitle">
            Tag subscribers with groups so you can email a specific squad,
            role, or whatever segment matters. One subscriber can be in
            many. Manage memberships on the Subscribers tab.
          </p>
        </div>
        <div className="head-actions">
          {!creating && (
            <button
              className="ebtn ebtn-primary"
              onClick={() => setCreating(true)}
            >
              + New group
            </button>
          )}
        </div>
      </div>

      {error && <div className="ealert ealert-error">{error}</div>}

      {creating && (
        <div className="card-surface create-card">
          <form onSubmit={handleCreate} className="create-form">
            <input
              type="text"
              placeholder="Group name (e.g. U12 parents)"
              value={newGroupName}
              onChange={e => setNewGroupName(e.target.value)}
              maxLength={60}
              autoFocus
              disabled={createBusy}
            />
            <button
              type="submit"
              className="ebtn ebtn-primary"
              disabled={createBusy || !newGroupName.trim()}
            >
              {createBusy ? 'Creating…' : 'Create group'}
            </button>
            <button
              type="button"
              className="ebtn ebtn-ghost"
              onClick={() => { setCreating(false); setNewGroupName(''); }}
              disabled={createBusy}
            >
              Cancel
            </button>
          </form>
        </div>
      )}

      {loading ? (
        <div className="card-surface loading-state">
          <div className="spinner" />
          <p>Loading groups…</p>
        </div>
      ) : (
        <div className="card-surface">
          <table className="groups-table">
            <thead>
              <tr>
                <th>Name</th>
                <th className="cell-count">Members</th>
                <th aria-label="Actions"></th>
              </tr>
            </thead>
            <tbody>
              {groups.map(g => (
                <tr key={g.groupId} className={g.isAuto ? 'row-auto' : ''}>
                  <td className="cell-name">
                    {g.isAuto ? (
                      <span className="group-name-auto">{g.name}</span>
                    ) : editingId === g.groupId ? (
                      <input
                        type="text"
                        value={editName}
                        onChange={e => setEditName(e.target.value)}
                        autoFocus
                        maxLength={60}
                        onBlur={() => handleSaveRename(g.groupId)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') handleSaveRename(g.groupId);
                          if (e.key === 'Escape') { setEditingId(null); setEditName(''); }
                        }}
                        className="inline-edit-input"
                      />
                    ) : (
                      <span
                        className="group-name editable"
                        onClick={() => { setEditingId(g.groupId); setEditName(g.name); }}
                        title="Click to rename"
                      >
                        {g.name}
                      </span>
                    )}
                    {g.isAuto && (
                      <span className="auto-badge" title="Automatic — every active subscriber is in this group">
                        auto
                      </span>
                    )}
                  </td>
                  <td className="cell-count">{g.memberCount}</td>
                  <td className="cell-actions">
                    {!g.isAuto && (
                      <button
                        className="ebtn ebtn-danger ebtn-sm"
                        onClick={() => handleDelete(g)}
                        title="Delete group"
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {explicitGroupCount === 0 && (
            <p className="empty-hint">
              Only "All members" exists. Create your first group above to start
              segmenting your audience.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
